import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import BeefreeSDK from '@beefree.io/sdk';
import { getBeeToken } from '../../redux/services';
import { useStableCallback } from '../../libs/react-use-extra';
import { customFonts } from './BEE_fonts';
import { GroupPermissionModal } from './groupPermissionModal';
import { useToast, logout } from '../../redux/action';
import { userPrivilegesSelector } from '../../redux/selector';
import newTemplate from './newTemplateFile';
import {
  saveRow,
  getRows,
  getFolders,
  deleteRow,
  editRow,
  updateSortOrder,
  getSortOrder,
} from './savedRows/api';
import SavedRowsModal from './savedRows';
import SortRowsModal from './savedRows/SortRowsModal';
import { useIsFeatureEnabled } from '../../libs/feature-toggles/react';
import Feature from '../../infrastructure/features';

export default function Bee({
  clientUid,
  onLoad,
  onSave,
  onChange,
  beeEditor,
  className,
  currentDesign,
  onComment,
  onSend,
  AB,
  draftId,
  handleBeeEditorInit,
  isBeeLoading,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isSA, isSU, canPublish } = useSelector(userPrivilegesSelector);
  const isSavedRowsFeatureEnabled = useIsFeatureEnabled(Feature.SAVED_ROWS);
  const dynamicRowsConfigurationInitialState = isSavedRowsFeatureEnabled
    ? {
        emptyRows: true,
        defaultRows: true,
        selectedRowType: 'defaultRows',
        externalContentURLs: [],
      }
    : {
        emptyRows: true,
        defaultRows: false,
      };
  const [open, setOpen] = useState(false);
  const [isSavedRowsModalVisible, setSavedRowsModalVisible] = useState(false);
  const [isSortModalVisible, setIsSortModalVisible] = useState(false);
  const [orgRows, setOrgRows] = useState([]);
  const [dynamicRowsConfiguration, setDynamicRowsConfiguration] = useState(
    dynamicRowsConfigurationInitialState
  );
  const [modalProps, setModalProps] = useState({});
  const isBeeEditorStarted = useRef(null);
  const rowSortOrderRef = useRef('date_desc');
  const sortResolveRef = useRef(null);
  // Set when we reload rows programmatically (e.g. after saving a row) so the
  // external-content request that BeeFree fires does not prompt for sort order.
  const suppressSortPromptRef = useRef(false);
  // Mirrors the latest rows configuration so BeeFree-registered handlers read
  // the current selected folder instead of a stale closure value.
  const dynamicRowsConfigRef = useRef(dynamicRowsConfigurationInitialState);
  const { errorToast, successToast } = useToast();

  useEffect(() => {
    const getAsyncDynamicRows = async () => {
      try {
        const [dynamicRows, rows, sortOrder] = await Promise.all([
          getFolders({ isSA, isSU, canPublish }),
          getRows(),
          getSortOrder(),
        ]);
        setDynamicRowsConfiguration(dynamicRows);
        setOrgRows(rows);
        rowSortOrderRef.current = sortOrder;
      } catch (e) {
        console.error('Error in getAsyncDynamicRows:', e);
        errorToast('Encountered an error while fetching saved rows.');
      }
    };
    if (isSavedRowsFeatureEnabled) {
      getAsyncDynamicRows();
    }
  }, [draftId, isBeeLoading]);

  useEffect(() => {
    dynamicRowsConfigRef.current = dynamicRowsConfiguration;
  }, [dynamicRowsConfiguration]);

  const displayResolution = useRef(null);

  const { userHandle, username, userColor, beeAdmin } = useSelector(state => ({
    userHandle: state.user.detail._id,
    username: `${state.user.detail.firstName} ${state.user.detail.lastName}`,
    userColor: state.user.detail.avatarColor,
    beeAdmin: state.user.userPrivileges?.beeAdmin,
  }));

  const editorFonts = {
    showDefaultFonts: true,
    customFonts: customFonts,
  };

  const sortRows = (rows, order) => {
    if (!rows?.length) return rows || [];
    const sorted = [...rows];
    switch (order) {
      case 'name_asc':
        return sorted.sort((a, b) =>
          (a.metadata?.name || '').localeCompare(b.metadata?.name || '')
        );
      case 'name_desc':
        return sorted.sort((a, b) =>
          (b.metadata?.name || '').localeCompare(a.metadata?.name || '')
        );
      case 'date_asc':
        return sorted.sort(
          (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
        );
      case 'date_desc':
      default:
        return sorted.sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
    }
  };

  const handleExternalContentURLs = (resolve, reject) => {
    // A programmatic rows reload (e.g. after saving a row) should not prompt
    // the user to choose a sort order — resolve silently with the current
    // folder, preserving the existing display order.
    if (suppressSortPromptRef.current) {
      suppressSortPromptRef.current = false;
      const currentConfig = dynamicRowsConfigRef.current;
      const currentHandle = currentConfig.selectedRowType;
      const currentFolder = currentConfig.externalContentURLs?.find(
        url => url.handle === currentHandle
      );
      if (currentFolder) {
        resolve(currentFolder);
      } else {
        reject();
      }
      return;
    }
    sortResolveRef.current = { resolve, reject };
    setIsSortModalVisible(true);
  };

  const handleSortSelect = sortOrder => {
    rowSortOrderRef.current = sortOrder;
    setIsSortModalVisible(false);
    updateSortOrder(sortOrder)
      .then(() => console.log('[SavedRows] sortOrder saved to DB:', sortOrder))
      .catch(console.error);

    const currentHandle = dynamicRowsConfiguration.selectedRowType;
    const currentFolder = dynamicRowsConfiguration.externalContentURLs?.find(
      url => url.handle === currentHandle
    );

    if (currentFolder && sortResolveRef.current) {
      sortResolveRef.current.resolve(currentFolder);
    } else {
      sortResolveRef.current?.reject();
    }
  };

  const handleSortCancel = () => {
    setIsSortModalVisible(false);
    sortResolveRef.current?.reject();
  };

  const handleGetRows = async (resolve, _reject, args) => {
    setDynamicRowsConfiguration(prevState => ({
      ...prevState,
      selectedRowType: args.handle,
    }));
    // Always fetch fresh. The cached orgRows is not updated when a row is saved,
    // so serving it here left newly saved rows invisible — most noticeably the
    // first row in a brand-new folder, where the folder appeared empty even
    // though the row was persisted.
    try {
      const rows = await getRows();
      setOrgRows(rows);
      resolve(sortRows(rows[args.handle] || [], rowSortOrderRef.current));
    } catch (e) {
      console.error('Error in handleGetRows:', e);
      errorToast('Encountered an error while fetching saved rows.');
    }
  };

  const handleSaveRow = async (resolve, reject, row) => {
    try {
      const result = await new Promise((modalResolve, modalReject) => {
        setModalProps({
          resolve: modalResolve,
          reject: modalReject,
          args: {
            row,
            isNew: true,
            onHide: () => setSavedRowsModalVisible(false),
          },
        });
        setSavedRowsModalVisible(true);
      });
      if (result && result.name && result.folder && result.folderId) {
        resolve(
          {
            name: result.name,
            id: uuidv4(),
            folder: result.folder,
            folderId: result.folderId,
          },
          { synced: false }
        );
      } else {
        reject('Save cancelled or no name provided');
      }
    } catch (error) {
      reject(error);
    } finally {
      setSavedRowsModalVisible(false);
    }
  };

  const handleSaveRowToDB = async rowTemplate => {
    try {
      const row = JSON.parse(rowTemplate);
      await saveRow({
        name: row.metadata.name,
        content: row,
        folderId: row.metadata.folderId,
        id: row.metadata.id,
      });
      // Refresh the saved-rows panel so the newly added row shows up in its
      // folder immediately, instead of only after switching folders. Suppress
      // the sort-order prompt that BeeFree would otherwise raise on reload.
      suppressSortPromptRef.current = true;
      beeEditor?.loadRows?.();
    } catch (e) {
      console.error('Error in handleSaveRowToDB:', e);
      errorToast('Encountered an error while saving a row.');
    }
  };

  const handleOnDeleteRow = async (resolve, reject, { row }) => {
    try {
      if (!row) return;
      // Publisher-folder rows are shown to consumers for use, but only users who
      // can manage the folder (canDelete) may remove them. Block here so we don't
      // fire a delete the backend would reject with a 403.
      const folder = dynamicRowsConfigRef.current.externalContentURLs?.find(
        f =>
          f.handle === row.metadata?.folderId ||
          f.value === row.metadata?.folderId
      );
      if (folder?.isPublisherFolder && folder.behaviors?.canDelete !== true) {
        errorToast("You don't have permission to delete rows in this folder.");
        return reject?.();
      }
      await deleteRow(row.metadata.id);
      successToast('Saved row successfully deleted.');
      resolve(true);
    } catch (e) {
      console.error('Error in handleOnDeleteRow:', e);
      errorToast('Encountered an error while deleting a row.');
    }
  };

  const handleOnEditRow = async (resolve, reject, { row }) => {
    if (!row) return;

    try {
      const result = await new Promise((modalResolve, modalReject) => {
        setModalProps({
          resolve: modalResolve,
          reject: modalReject,
          args: {
            row,
            onHide: () => setSavedRowsModalVisible(false),
          },
        });
        setSavedRowsModalVisible(true);
      });
      if (result && result.name && result.folder && result.folderId) {
        row.metadata.name = result.name;
        row.metadata.folder = result.folder;
        row.metadata.folderId = result.folderId;
        await editRow({
          rowId: row.metadata.id,
          content: row,
          name: result.name,
          folderId: result.folderId,
        });
        resolve(true);
      } else {
        reject('Edit cancelled or no name provided');
      }
    } catch (error) {
      errorToast('Encountered an error while editing a row.');
      reject(error);
    } finally {
      setSavedRowsModalVisible(false);
    }
  };

  const hooks = {
    getRows: {
      handler: handleGetRows,
    },
  };

  const onCloseConditionDialog = () => {
    setOpen(false);
    displayResolution.current?.reject();
  };

  const saveDisplayConditions = (groupIds, groupNames) => {
    if (!groupIds?.length) displayResolution.current?.reject();
    displayResolution.current?.resolve({
      type: 'group',
      label: 'Applied Group Filters',
      description: groupNames?.join(', ') ?? '',
      before: `{{#if (subscribedToGroups '${groupIds.join(',')}') }}`,
      after: '{{/if}}',
    });
    setOpen(false);
  };

  const beeConfig = {
    uid: clientUid,
    container: 'bee-plugin-container',
    language: 'en-US',
    translations: {
      'mailup-bee-common-widgets-image-selector': {
        edit: 'Edit Image',
      },
    },
    roleHash: beeAdmin ? 'nr_admin_0' : 'nr_staff_0',
    username,
    userColor,
    userHandle,
    trackChanges: true,
    contentDialog: {
      externalContentURLs: {
        label: '⇅ Choose Saved Row Display Order',
        handler: handleExternalContentURLs,
      },
      rowDisplayConditions: {
        label: 'Choose Conditions',
        handler: (resolve, reject) => {
          setOpen(true);
          displayResolution.current = { resolve, reject };
        },
      },
      saveRow: {
        handler: handleSaveRow,
      },
      onDeleteRow: {
        handler: handleOnDeleteRow,
      },
      onEditRow: {
        handler: handleOnEditRow,
      },
    },
    editorFonts,
    onSave: useStableCallback(onSave),
    onChange: useStableCallback(onChange),
    onLoad: useStableCallback(() => {
      onLoad();
      isBeeEditorStarted.current = true;
    }),
    onComment: useStableCallback(onComment),
    onSend: useStableCallback(onSend),
    onError: async error => {
      if (error.status_code === 401) {
        // refresh token if it is expired (after 12 hours)
        try {
          const token = await getBeeToken(clientUid);
          beeEditor.updateToken(token);
        } catch (refreshError) {
          console.error('Authentication failed:', refreshError);
          dispatch(logout(history));
        }
      } else {
        // log all other errors
        console.error('BEE Editor error:', error);
      }
    },
    onWarning: warning => console.warn('bee editor warning:', warning),
    onSaveRow: handleSaveRowToDB,
    loadingSpinnerDisableOnDialog: true,
    saveRows: true,
    rowsConfiguration: {
      ...dynamicRowsConfiguration,
      maxRowsDisplayed: 100,
    },
    hooks,
  };

  const loadEditor = async () => {
    const loadTemplate =
      !currentDesign?.page || currentDesign === '{}'
        ? newTemplate
        : currentDesign;

    await beeEditor.start(beeConfig, loadTemplate).catch(console.error);
  };

  const loadBeeConfig = async () => {
    // we need to check if isBeeEditorStarted.current is true then loadConfig for dynamicRowsConfiguration, otherwise retry it again in a second
    const checkInterval = setInterval(async () => {
      if (beeEditor && isBeeEditorStarted.current) {
        clearInterval(checkInterval);
        await beeEditor.loadConfig(beeConfig);
      }
    }, 1000);
  };

  useEffect(() => {
    async function initializeEditor() {
      const token = await getBeeToken(clientUid);
      beeEditor = new BeefreeSDK(token);
      handleBeeEditorInit(beeEditor);
      loadEditor();
    }
    initializeEditor();
  }, []);

  useEffect(() => {
    // reloads editor when AB version changes
    if (AB) {
      loadEditor();
    }
  }, [AB]);

  useEffect(() => {
    // reloads editor when dynamicRowsConfiguration changes
    if (dynamicRowsConfiguration?.externalContentURLs?.length) {
      loadBeeConfig();
    }
  }, [dynamicRowsConfiguration]);

  return (
    <>
      <div id="bee-plugin-container" className={className} />
      {isSavedRowsModalVisible && (
        <SavedRowsModal
          {...modalProps}
          setDynamicRowsConfiguration={setDynamicRowsConfiguration}
          dynamicRowsConfiguration={{
            ...dynamicRowsConfiguration,
            // Only folders the user can actually save into are valid targets:
            // universal → SAs only; publisher → owning org or superuser (driven
            // by behaviors.canEdit from getFolders); regular org folders → yes.
            externalContentURLs:
              dynamicRowsConfiguration.externalContentURLs?.filter(folder => {
                if (folder.isUniversal) return isSA;
                if (folder.isPublisherFolder)
                  return folder.behaviors?.canEdit === true;
                return true;
              }),
          }}
          orgRows={orgRows}
        />
      )}
      <SortRowsModal
        isVisible={isSortModalVisible}
        currentSortOrder={rowSortOrderRef.current}
        onSelect={handleSortSelect}
        onClose={handleSortCancel}
      />
      <GroupPermissionModal
        isOpen={open}
        handleClose={onCloseConditionDialog}
        onSave={saveDisplayConditions}
      />
    </>
  );
}
